import React from 'react'
import { useSelector } from 'react-redux'
import { Box, Flex, PseudoBox, Text } from '@chakra-ui/core'
import { RootState } from 'constants/interfaces'
import LocaleText from 'components/LocaleText'
import { NotificationServiceIcon } from 'components/Notifications/NotificationServiceIcon'
import { useDateLocale } from 'hooks/useDateLocale'
import { isRtlSelector } from 'redux/selectors/general'
import { notifTokens } from 'services/notifications/designTokens'
import { getNotificationVisual } from 'services/notifications/serviceVisuals'
import { INotification } from 'services/notifications/types'
import { formatNotificationRowDate } from 'services/notifications/formatNotificationTime'
import { resolveNotificationTitle } from 'services/notifications/notificationTitle'

interface IProps {
  notification: INotification
  onClick: () => void
  // Desktop list only: the row currently mirrored in the preview column.
  isSelected?: boolean
}

// Row anatomy mirrors mobile Notification Center: unread carries a service-colored icon, service
// text, and dot. Read falls back to neutral icon/service tones so the list keeps its footprint.
export const NotificationRow: React.FC<IProps> = ({ notification, onClick, isSelected }) => {
  const isRtl = useSelector(isRtlSelector)
  const visual = getNotificationVisual(notification, isRtl)
  const isUnread = !notification.seen
  const locale = useSelector((state: RootState) => state.general.locale)
  const translations = useSelector((state: RootState) => state.config.translations)
  // Registers dayjs's locale data file (month/day names) so formatNotificationRowDate's
  // `.locale(locale)` call below actually resolves instead of silently falling back to English.
  useDateLocale(locale)
  const serviceColor = isUnread ? visual.text : notifTokens.faint
  const serviceLabel = translations?.[locale]?.[visual.label] ?? visual.label
  const title = resolveNotificationTitle(notification, translations?.[locale] as Record<string, string>, serviceLabel)

  return (
    <PseudoBox
      as="button"
      data-notif-row
      onClick={onClick}
      display="flex"
      w="100%"
      textAlign="left"
      alignItems="center"
      style={{ gap: '16px' }}
      p="13px 12px"
      mb="6px"
      minH="86px"
      borderRadius="14px"
      cursor="pointer"
      bg={isSelected ? 'white' : isUnread ? notifTokens.rowUnread : 'transparent'}
      boxShadow={isSelected ? `0 1px 2px rgba(15,23,42,.06), 0 0 0 1px ${notifTokens.border}` : 'none'}
      _hover={{ bg: isSelected ? 'white' : notifTokens.rowHover }}
    >
      <NotificationServiceIcon visual={visual} isUnread={isUnread} senderId={notification.sender_id} isRtl={isRtl} />

      <Box flex="1" minW="0" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Flex alignItems="center" style={{ columnGap: '5px' }}>
          <Text fontSize="11.5px" fontWeight={600} color={serviceColor}>
            <LocaleText text={visual.label} />
          </Text>
          <Text fontSize="11.5px" fontWeight={600} color={notifTokens.faint}>
            ·
          </Text>
          <Text fontSize="11.5px" color={notifTokens.faint} style={{ unicodeBidi: 'plaintext' }}>
            {formatNotificationRowDate(notification.created_at_ts, locale)}
          </Text>
        </Flex>

        <Flex alignItems="center" justifyContent="space-between" style={{ columnGap: '8px' }}>
          <Text
            fontWeight={isUnread ? 700 : 600}
            fontSize="13.5px"
            color={notifTokens.ink}
            isTruncated
            textAlign={isRtl ? 'right' : 'left'}
            style={{ unicodeBidi: 'plaintext' }}
          >
            {title}
          </Text>
          <Box w="6px" h="6px" borderRadius="50%" bg={isUnread ? visual.text : 'transparent'} flexShrink={0} />
        </Flex>

        {notification.body && (
          <Text
            fontSize="12.5px"
            lineHeight="18px"
            color={notifTokens.muted}
            textAlign={isRtl ? 'right' : 'left'}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              unicodeBidi: 'plaintext',
            }}
          >
            {notification.body}
          </Text>
        )}

      </Box>
    </PseudoBox>
  )
}
